import React, { useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import WrapperComponent from '../commonComponents/WrapperComponent';
import { url } from '../../util/config';
import { FaEye, FaCopy, FaToggleOn, FaToggleOff, FaTrash, FaSpinner, FaCloudUploadAlt, FaTimes, FaSyncAlt } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './MediaComponent.css';

const MediaComponent = () => {
    const [mediaList, setMediaList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [toggling, setToggling] = useState(null);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadFiles, setUploadFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [mediaName, setMediaName] = useState('');
    const [mediaDescription, setMediaDescription] = useState('');
    const [syncing, setSyncing] = useState(null);

    // Fetch all media
    const fetchAllMedia = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`${url}/getallmedia`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch media');
            }

            const data = await response.json();
            const mappedData = Array.isArray(data) ? data.map(item => ({
                ...item,
                mediaPath: item.fileUrl,
                mediaURL: item.fileUrl,
                mediaName: item.filename,
                createdAt: item.timestamp 
            })) : data.media || [];
            setMediaList(mappedData);
        } catch (error) {
            console.error('Error fetching media:', error);
            toast.error('Failed to fetch media', { autoClose: 3000 });
            setMediaList([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAllMedia();
    }, []);

    // Handle file input change
    const handleFileChange = (e) => {
        const files = Array.from(e.target.files || []);
        setUploadFiles(prev => [...prev, ...files]);
    };

    const handleSyncMediaUrl = async (media) => {
        try {
            setSyncing(media._id);
            const token = localStorage.getItem('token');
            const response = await fetch(`${url}/getmediaurl/${media.mediaID}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
            });

            if (!response.ok) {
            throw new Error('Failed to sync media URL');
            }

            const data = await response.json();
            const newUrl = data.mediaURL || data.fileUrl || data.url;
            if (!newUrl) throw new Error('API missing media URL');

            setMediaList(prev =>
            prev.map(item =>
                item._id === media._id
                ? { ...item, mediaURL: newUrl, mediaPath: newUrl }
                : item
            )
            );

            toast.success('Media URL synced', { autoClose: 2000 });

            handleCopyUrl(newUrl, media.mediaName);
        } catch (err) {
            console.error('Error syncing media URL:', err);
            toast.error(err.message || 'Sync failed', { autoClose: 2000 });
        } finally {
            setSyncing(null);
        }
    };

    // Handle drag and drop
    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        const files = Array.from(e.dataTransfer.files || []);
        const imageFiles = files.filter(file => file.type.startsWith('image/'));
        if (imageFiles.length !== files.length) {
            toast.warning('Only image files are supported', { autoClose: 2000 });
        }
        setUploadFiles(prev => [...prev, ...imageFiles]);
    };

    // Remove file from upload list
    const removeFile = (index) => {
        setUploadFiles(prev => prev.filter((_, i) => i !== index));
    };

    // Upload media files
    const handleUploadMedia = async (e) => {
        e.preventDefault();

        if (uploadFiles.length === 0) {
            toast.error('Please select at least one file', { autoClose: 2000 });
            return;
        }

        if (!mediaName.trim()) {
            toast.error('Please enter a media collection name', { autoClose: 2000 });
            return;
        }

        try {
            setUploading(true);
            const token = localStorage.getItem('token');

            // Upload each file
            for (let file of uploadFiles) {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('mediaName', `${mediaName} - ${file.name}`);
                formData.append('mediaDescription', mediaDescription);

                const response = await fetch(`${url}/media`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                    body: formData
                });

                if (!response.ok) {
                    const error = await response.json().catch(() => ({}));
                    throw new Error(error.message || `Failed to upload ${file.name}`);
                }

                const data = await response.json();
                console.log('Upload response:', data);
            }

            toast.success(`${uploadFiles.length} file(s) uploaded successfully!`, { autoClose: 3000 });
            
            // Reset form
            setUploadFiles([]);
            setMediaName('');
            setMediaDescription('');
            setShowUploadModal(false);

            // Refresh media list
            await fetchAllMedia();
        } catch (error) {
            console.error('Error uploading media:', error);
            toast.error(error.message || 'Failed to upload media', { autoClose: 3000 });
        } finally {
            setUploading(false);
        }
    };

    // Copy media URL to clipboard
    const handleCopyUrl = async (mediaUrl, mediaName) => {
        try {
            await navigator.clipboard.writeText(mediaUrl);
            toast.success(`${mediaName} URL copied!`, { autoClose: 2000 });
        } catch {
            toast.error('Failed to copy URL', { autoClose: 2000 });
        }
    };

    // Toggle media status
    const handleToggleStatus = async (mediaId, currentStatus) => {
        try {
            setToggling(mediaId);
            const token = localStorage.getItem('token');
            const response = await fetch(`${url}/media/${mediaId}/flag`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ status: !currentStatus })
            });

            if (!response.ok) {
                throw new Error('Failed to toggle status');
            }

            // Update local state
            setMediaList(prev =>
                prev.map(item =>
                    item._id === mediaId ? { ...item, status: !currentStatus } : item
                )
            );
            toast.success('Status updated!', { autoClose: 2000 });
        } catch (error) {
            console.error('Error toggling status:', error);
            toast.error('Failed to toggle status', { autoClose: 2000 });
        } finally {
            setToggling(null);
        }
    };

    // Delete media
    const handleDeleteMedia = async (mediaId, mediaName) => {
        if (!window.confirm(`Are you sure you want to delete "${mediaName}"?`)) {
            return;
        }

        try {
            setDeleting(mediaId);
            const token = localStorage.getItem('token');
            const response = await fetch(`${url}/media/${mediaId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to delete media');
            }

            // Remove from local state
            setMediaList(prev => prev.filter(item => item._id !== mediaId));
            toast.success('Media deleted successfully!', { autoClose: 2000 });
        } catch (error) {
            console.error('Error deleting media:', error);
            toast.error('Failed to delete media', { autoClose: 2000 });
        } finally {
            setDeleting(null);
        }
    };

    // Format date
    const formatDate = (timestamp) => {
        const date = new Date(timestamp);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <WrapperComponent>
            <div className="media-container">
                {/* Header */}
                <div className="media-header">
                    <div className="header-content">
                        <h1 className="header-title">
                            <i className="fas fa-images me-3"></i>Media Library
                        </h1>
                        <p className="header-subtitle">Manage and organize your media files</p>
                    </div>
                    <div className="header-actions">
                        <button 
                            className="btn btn-gradient-ai me-3"
                            onClick={() => setShowUploadModal(true)}
                        >
                            <FaCloudUploadAlt className="me-2" />
                            Upload Media
                        </button>
                        <button 
                            className="btn btn-gradient-ai"
                            onClick={fetchAllMedia}
                            disabled={loading}
                        >
                            <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-sync-alt'} me-2`}></i>
                            {loading ? 'Refreshing...' : 'Refresh'}
                        </button>
                    </div>
                </div>

                {/* Loading State */}
                {loading ? (
                    <div className="loading-container">
                        <div className="spinner-ai">
                            <FaSpinner className="spinner-icon" />
                        </div>
                        <p className="loading-text">Loading your media...</p>
                    </div>
                ) : mediaList.length === 0 ? (
                    <div className="empty-state">
                        <i className="fas fa-inbox"></i>
                        <h3>No Media Found</h3>
                        <p>Start uploading media to see it here</p>
                        <button 
                            className="btn btn-gradient-ai mt-3"
                            onClick={() => setShowUploadModal(true)}
                        >
                            <FaCloudUploadAlt className="me-2" />
                            Upload Your First Media
                        </button>
                    </div>
                ) : (
                    <div className="media-grid">
                        {mediaList.map((media) => (
                            <div key={media._id} className="media-card">
                                {/* Image Container */}
                                <div className="media-image-container">
                                    <img
                                        src={media.mediaPath || '/placeholder.jpg'}
                                        alt={media.mediaName}
                                        className="media-image"
                                        onError={(e) => {
                                            e.target.src = '/placeholder.jpg';
                                        }}
                                    />
                                    <div className="media-overlay">
                                        <button
                                            className="overlay-btn view-btn"
                                            onClick={() => setSelectedImage(media)}
                                            title="View Full Image"
                                        >
                                            <FaEye />
                                        </button>
                                    </div>
                                    <div className="status-badge">
                                        <span className={`badge ${media.status ? 'badge-active' : 'badge-inactive'}`}>
                                            {media.status ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                </div>

                                {/* Card Content */}
                                <div className="media-content">
                                    <h5 className="media-name" title={media.mediaName}>
                                        {media.mediaName}
                                    </h5>
                                    <p className="media-date">
                                        <i className="fas fa-calendar me-2"></i>
                                        {formatDate(media.createdAt)}
                                    </p>

                                    {/* Actions */}
                                    <div className="media-actions">
                                        {media.status === 'uploaded' ? (
                                            <button
                                                className="action-btn sync-btn"
                                                onClick={() => handleSyncMediaUrl(media)}
                                                disabled={syncing === media.mediaID}
                                                title="Sync media URL"
                                            >
                                                {syncing === media.mediaID ? <FaSpinner className="fa-spin" /> : <FaSyncAlt />}
                                            </button>
                                            ) : media.mediaURL ? (
                                            <button
                                                className="action-btn copy-btn"
                                                onClick={() => handleCopyUrl(media.mediaURL, media.mediaName)}
                                                title="Copy URL"
                                            >
                                                <FaCopy />
                                            </button>
                                            ) : null}
                                        <button
                                            className={`action-btn toggle-btn ${media.status ? 'active' : 'inactive'}`}
                                            onClick={() => handleToggleStatus(media._id, media.status)}
                                            disabled={toggling === media._id}
                                            title={media.status ? 'Deactivate' : 'Activate'}
                                        >
                                            {toggling === media._id ? (
                                                <FaSpinner className="fa-spin" />
                                            ) : media.status ? (
                                                <FaToggleOn />
                                            ) : (
                                                <FaToggleOff />
                                            )}
                                        </button>
                                        <button
                                            className="action-btn delete-btn"
                                            onClick={() => handleDeleteMedia(media._id, media.mediaName)}
                                            disabled={deleting === media._id}
                                            title="Delete Media"
                                        >
                                            {deleting === media._id ? (
                                                <FaSpinner className="fa-spin" />
                                            ) : (
                                                <FaTrash />
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Image Modal */}
                {selectedImage && (
                    <div className="modal-overlay" onClick={() => setSelectedImage(null)}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <button
                                className="modal-close"
                                onClick={() => setSelectedImage(null)}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                            <img
                                src={selectedImage.mediaPath}
                                alt={selectedImage.mediaName}
                                className="modal-image"
                            />
                            <div className="modal-info">
                                <h4>{selectedImage.mediaName}</h4>
                                <p>{formatDate(selectedImage.createdAt)}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Upload Modal */}
                {showUploadModal && (
                    <div className="upload-modal-overlay" onClick={() => !uploading && setShowUploadModal(false)}>
                        <div className="upload-modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="upload-modal-header">
                                <h2>
                                    <FaCloudUploadAlt className="me-2" />
                                    Upload Media
                                </h2>
                                <button
                                    className="upload-modal-close"
                                    onClick={() => !uploading && setShowUploadModal(false)}
                                    disabled={uploading}
                                >
                                    <FaTimes />
                                </button>
                            </div>

                            <form onSubmit={handleUploadMedia}>
                                {/* Media Collection Name */}
                                <div className="upload-form-group">
                                    <label className="upload-label">Media Collection Name *</label>
                                    <input
                                        type="text"
                                        className="upload-input"
                                        placeholder="e.g., Product Photos, Marketing Materials"
                                        value={mediaName}
                                        onChange={(e) => setMediaName(e.target.value)}
                                        disabled={uploading}
                                        required
                                    />
                                    <small className="upload-helper">Give your media collection a meaningful name</small>
                                </div>

                                {/* Drag and Drop Area */}
                                <div
                                    className={`upload-drop-area ${dragActive ? 'active' : ''}`}
                                    onDragEnter={handleDrag}
                                    onDragLeave={handleDrag}
                                    onDragOver={handleDrag}
                                    onDrop={handleDrop}
                                >
                                    <input
                                        type="file"
                                        multiple
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        disabled={uploading}
                                        className="upload-file-input"
                                        id="fileInput"
                                    />
                                    <label htmlFor="fileInput" className="upload-drop-label">
                                        <FaCloudUploadAlt className="upload-icon" />
                                        <h4>Drag and drop your images here</h4>
                                        <p>or click to browse</p>
                                        <small>Supported: JPG, PNG, GIF, WebP (Max 5MB each)</small>
                                    </label>
                                </div>

                                {/* File List */}
                                {uploadFiles.length > 0 && (
                                    <div className="upload-files-list">
                                        <h5>Selected Files ({uploadFiles.length})</h5>
                                        {uploadFiles.map((file, index) => (
                                            <div key={index} className="upload-file-item">
                                                <div className="upload-file-info">
                                                    <i className="fas fa-image"></i>
                                                    <span>{file.name}</span>
                                                    <small>
                                                        {(file.size / 1024 / 1024).toFixed(2)} MB
                                                    </small>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="upload-file-remove"
                                                    onClick={() => removeFile(index)}
                                                    disabled={uploading}
                                                >
                                                    <FaTimes />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Action Buttons */}
                                <div className="upload-modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setShowUploadModal(false)}
                                        disabled={uploading}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-gradient-ai"
                                        disabled={uploading || uploadFiles.length === 0}
                                    >
                                        {uploading ? (
                                            <>
                                                <FaSpinner className="fa-spin me-2" />
                                                Uploading...
                                            </>
                                        ) : (
                                            <>
                                                <FaCloudUploadAlt className="me-2" />
                                                Upload {uploadFiles.length} File(s)
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                <ToastContainer />
            </div>
        </WrapperComponent>
    );
};

export default MediaComponent;